import { supabase } from "@/integrations/supabase/client";

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  priority: "low" | "normal" | "high" | "urgent";
  title: string;
  message: string;
  action_url?: string;
  action_label?: string;
  read: boolean;
  metadata?: Record<string, any>;
  created_at: string;
}

export async function createNotification(
  userId: string,
  notification: Omit<Notification, "id" | "user_id" | "created_at" | "read">
): Promise<void> {
  const { error } = await supabase
    .from("notifications" as any)
    .insert([{
      user_id: userId,
      ...notification,
    }]);

  if (error) throw error;
}

export async function getUnreadNotifications(userId: string): Promise<Notification[]> {
  const { data, error } = await supabase
    .from("notifications" as any)
    .select("*")
    .eq("user_id", userId)
    .eq("read", false)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data as any[]) as Notification[];
}

export async function getAllNotifications(userId: string, limit = 50): Promise<Notification[]> {
  const { data, error } = await supabase
    .from("notifications" as any)
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data as any[]) as Notification[];
}

export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from("notifications" as any)
    .update({ read: true })
    .eq("id", notificationId);

  if (error) throw error;
}

export async function markAllAsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from("notifications" as any)
    .update({ read: true })
    .eq("user_id", userId)
    .eq("read", false);

  if (error) throw error;
}

export async function deleteNotification(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from("notifications" as any)
    .delete()
    .eq("id", notificationId);

  if (error) throw error;
}

// Helper to send notifications to multiple users (e.g., all compliance officers)
export async function notifyUsersByRole(
  role: "athletic_director" | "coach" | "compliance_officer" | "super_admin",
  notification: Omit<Notification, "id" | "user_id" | "created_at" | "read">
): Promise<void> {
  const { data: userRoles } = await supabase
    .from("user_roles")
    .select("user_id")
    .eq("role", role as any);

  if (!userRoles || userRoles.length === 0) return;

  const notifications = userRoles.map((ur: any) => ({
    user_id: ur.user_id,
    ...notification,
  }));

  const { error } = await supabase
    .from("notifications" as any)
    .insert(notifications);

  if (error) throw error;
}
